import shajs from "sha.js"
import { performance, targeting } from "cookiesConsent/domain/consent"
import { latestConsent } from "cookiesConsent/features/getConsent/latestConsent"
import { FeatureFlagReadModel, FeatureFlagReadModelFactory } from "featureFlags"
import { Analytics } from "cookiesConsent/domain/analytics"
import {
  Customer,
  EmailAddress,
  Guest,
  Visitor
} from "cookiesConsent/domain/visitor"
import { analyticsForThisPage } from "cookiesConsent/infrastructure/segment/analyticsForThisPage"

declare global {
  interface Window {
    ometria?: {
      identify?: (email: string) => void
    }
  }
}

export interface VisitorIdentity {
  email: string
  id?: string | number | null
}

interface IdentityAnnouncement {
  announce: (visitor: VisitorIdentity) => void
}

const hashed = (email: string): string =>
  shajs(`sha256`).update(email).digest(`hex`)

class SegmentIdentity implements IdentityAnnouncement {
  announce = ({ email, id }: VisitorIdentity): void => {
    if (!id) {
      this.withhold()
    } else {
      window?.analytics?.identify?.(id, { email, hashedEmail: hashed(email) })
    }
  }

  private withhold = (): void => {}
}

class GtmHashedEmail implements IdentityAnnouncement {
  announce = ({ email }: VisitorIdentity): void => {
    window?.dataLayer?.push?.({ hashedEmail: hashed(email) })
  }
}

class OmetriaIdentity implements IdentityAnnouncement {
  announce = ({ email }: VisitorIdentity): void => {
    window?.ometria?.identify?.(email)
  }
}

class ConsentGatedIdentity implements IdentityAnnouncement {
  announce = (visitor: VisitorIdentity): void => {
    const consent = latestConsent.read()

    if (consent.permits(performance)) {
      new SegmentIdentity().announce(visitor)
    } else {
      this.withhold()
    }

    if (consent.permits(targeting)) {
      new GtmHashedEmail().announce(visitor)
      new OmetriaIdentity().announce(visitor)
    } else {
      this.withhold()
    }
  }

  private withhold = (): void => {}
}

const visitorFrom = ({ email, id }: VisitorIdentity): Visitor => {
  if (id) {
    return new Customer(id, new EmailAddress(email))
  } else {
    return new Guest(new EmailAddress(email))
  }
}

class AnalyticsBackedIdentity implements IdentityAnnouncement {
  private readonly analytics: Analytics

  constructor(analytics: Analytics) {
    this.analytics = analytics
  }

  announce = (visitor: VisitorIdentity): void => {
    this.analytics.identify(visitorFrom(visitor))

    if (latestConsent.read().permits(targeting)) {
      new GtmHashedEmail().announce(visitor)
      new OmetriaIdentity().announce(visitor)
    } else {
      this.withhold()
    }
  }

  private withhold = (): void => {}
}

class IdentityAnnouncementFactory {
  private readonly featureFlags: FeatureFlagReadModel

  constructor(featureFlags: FeatureFlagReadModel) {
    this.featureFlags = featureFlags
  }

  build = (): IdentityAnnouncement => {
    if (
      this.featureFlags.getByName(`ENABLE_GUEST_IDENTIFY_WEB_5097`).isEnabled()
    ) {
      return new AnalyticsBackedIdentity(analyticsForThisPage())
    } else {
      return new ConsentGatedIdentity()
    }
  }
}

export const identifyVisitor = (visitor: VisitorIdentity): void =>
  new IdentityAnnouncementFactory(new FeatureFlagReadModelFactory().build())
    .build()
    .announce(visitor)
